import React from "react";
import {
  XYPlot,
  VerticalGridLines,
  HorizontalGridLines,
  XAxis,
  YAxis,
  ChartLabel,
  DiscreteColorLegend,
  HorizontalBarSeries,
  LabelSeries,
} from "react-vis";
import "react-vis/dist/style.css";
import AutoSizer from "react-virtualized/dist/commonjs/AutoSizer";

import { round } from "../../../store/reducers/dataReducer";

const days = [
  "Sunday",
  "Saturday",
  "Friday",
  "Thursday",
  "Wednesday",
  "Tuesday",
  "Monday",
];

const legendColors = ["#6785b6", "#849cc4", "#a2b4d3", "#bfcce1", "#dee5f0"];

const ages = [
  "Gen Z or Younger",
  "Millenials",
  "Gen X",
  "Baby Boomers",
  "Elders",
];

const incomes = ["High Income", "Medium Income", "Low Income"];

const GenderPlot = (props) => {
  const { width, height, stores } = props;

  const data1 = stores[0]["Gender"].map((g, index) => ({
    y: -0.5 - index,
    x: g,
  }));

  const labelData1 = stores[0]["Gender"].map((g, index) => ({
    y: -0.5 - index,
    x: g / 2,
    label: `${g}%`,
  }));

  const data2 = stores[0]["Gender"].map((g, index) => ({
    y: -0.5 - index,
    x: 100 - g,
  }));

  const labelData2 = stores[0]["Gender"].map((g, index) => ({
    y: -0.5 - index,
    x: 50,
    label: `${Number(round(100 - g).toFixed(2))}%`,
  }));

  const legend = [
    {
      title: "Female",
      color: legendColors[0],
      strokeWidth: 24,
    },
    { title: "Male", color: legendColors[4], strokeWidth: 24 },
  ];

  return (
    <XYPlot
      width={width - 10}
      height={height - 20}
      margin={{ left: 90, top: 50 }}
      stackBy="x"
      xDomain={[0, 100]}
      yDomain={[-7, 0]}
    >
      <VerticalGridLines />
      <HorizontalGridLines />
      <XAxis
        hideTicks
        orientation="top"
        style={{
          line: { stroke: "black", strokeWidth: 1 },
        }}
      />
      <ChartLabel
        text="Gender"
        includeMargin={true}
        xPercent={0.12}
        yPercent={-0.08}
      />
      <YAxis
        style={{
          line: { stroke: "black", strokeWidth: 1 },
          text: {
            stroke: "none",
            fill: "black",
            fontSize: 14,
            fontWeight: 400,
          },
        }}
        tickValues={[-0.5, -1.5, -2.5, -3.5, -4.5, -5.5, -6.5]}
        tickFormat={(t) => days[t + 6.5]}
      />
      <HorizontalBarSeries
        color={legendColors[0]}
        data={data1}
        barWidth={0.6}
        style={{
          text: { fontWeight: 800 },
        }}
      />
      <LabelSeries
        data={labelData1}
        labelAnchorX="middle"
        labelAnchorY="middle"
        style={{ fill: "white", fontSize: 14, fontWeight: 400 }}
      />
      <HorizontalBarSeries
        color={legendColors[4]}
        data={data2}
        barWidth={0.6}
        style={{
          text: { fontWeight: 800 },
        }}
      />
      <LabelSeries
        data={labelData2}
        labelAnchorX="middle"
        labelAnchorY="middle"
        style={{ fill: "black", fontSize: 14, fontWeight: 400 }}
      />
      <DiscreteColorLegend
        items={legend}
        orientation="horizontal"
        style={{ position: "absolute", left: 70, bottom: -20 }}
      />
    </XYPlot>
  );
};

const AgePlot = (props) => {
  const { width, height, stores } = props;

  const data = ages.map((age) =>
    stores[0][age].map((g, index) => ({
      y: -0.5 - index,
      x: g,
    }))
  );

  const labelData = ages.map((age, ageIndex) =>
    stores[0][age].map((g, index) => ({
      y: -0.5 - index,
      x:
        ageIndex === 0
          ? g / 2
          : g / 2 + stores[0][ages[ageIndex - 1]][index] / 2,
      label: `${g}%`,
    }))
  );

  const legend = ages.map((age, index) => ({
    title: age,
    color: legendColors[index],
    strokeWidth: 24,
  }));

  return (
    <XYPlot
      width={width - 10}
      height={height - 20}
      margin={{ left: 90, top: 50 }}
      stackBy="x"
      xDomain={[0, 100]}
      yDomain={[-7, 0]}
    >
      <VerticalGridLines />
      <HorizontalGridLines />
      <XAxis
        hideTicks
        orientation="top"
        style={{
          line: { stroke: "black", strokeWidth: 1 },
        }}
      />
      <ChartLabel
        text="Age group"
        includeMargin={true}
        xPercent={0.12}
        yPercent={-0.08}
      />
      <YAxis
        style={{
          line: { stroke: "black", strokeWidth: 1 },
          text: {
            stroke: "none",
            fill: "black",
            fontSize: 14,
            fontWeight: 400,
          },
        }}
        tickValues={[-0.5, -1.5, -2.5, -3.5, -4.5, -5.5, -6.5]}
        tickFormat={(t) => days[t + 6.5]}
      />
      {data.map((d, index) => (
        <HorizontalBarSeries
          key={index}
          color={legendColors[index]}
          data={d}
          barWidth={0.6}
          style={{
            text: { fontWeight: 800 },
          }}
        />
      ))}
      {labelData.map((d, index) => (
        <LabelSeries
          key={index}
          data={d}
          labelAnchorX="middle"
          labelAnchorY="middle"
          style={{
            fill: index === 0 ? "white" : "black",
            fontSize: 14,
            fontWeight: 400,
          }}
        />
      ))}
      <DiscreteColorLegend
        items={legend}
        orientation="horizontal"
        style={{ position: "absolute", left: 70, bottom: -20 }}
      />
    </XYPlot>
  );
};

const IncomePlot = (props) => {
  const { width, height, stores } = props;

  const data = incomes.map((income) =>
    stores[0][income].map((g, index) => ({
      y: -0.5 - index,
      x: g,
    }))
  );

  const labelData = incomes.map((income, incomeIndex) =>
    stores[0][income].map((g, index) => ({
      y: -0.5 - index,
      x:
        incomeIndex === 0
          ? g / 2
          : g / 2 + stores[0][incomes[incomeIndex - 1]][index] / 2,
      label: `${g}%`,
    }))
  );

  const legend = incomes.map((income, index) => ({
    title: income,
    color: legendColors[index * 2],
    strokeWidth: 24,
  }));

  return (
    <XYPlot
      width={width - 10}
      height={height - 20}
      margin={{ left: 90, top: 50 }}
      stackBy="x"
      xDomain={[0, 100]}
      yDomain={[-7, 0]}
    >
      <VerticalGridLines />
      <HorizontalGridLines />
      <XAxis
        hideTicks
        orientation="top"
        style={{
          line: { stroke: "black", strokeWidth: 1 },
        }}
      />
      <ChartLabel
        text="Income group"
        includeMargin={true}
        xPercent={0.12}
        yPercent={-0.08}
      />
      <YAxis
        style={{
          line: { stroke: "black", strokeWidth: 1 },
          text: {
            stroke: "none",
            fill: "black",
            fontSize: 14,
            fontWeight: 400,
          },
        }}
        tickValues={[-0.5, -1.5, -2.5, -3.5, -4.5, -5.5, -6.5]}
        tickFormat={(t) => days[t + 6.5]}
      />
      {data.map((d, index) => (
        <HorizontalBarSeries
          key={index}
          color={legendColors[index * 2]}
          data={d}
          barWidth={0.6}
          style={{
            text: { fontWeight: 800 },
          }}
        />
      ))}
      {labelData.map((d, index) => (
        <LabelSeries
          key={index}
          data={d}
          labelAnchorX="middle"
          labelAnchorY="middle"
          style={{
            fill: index === 0 ? "white" : "black",
            fontSize: 14,
            fontWeight: 400,
          }}
        />
      ))}
      <DiscreteColorLegend
        items={legend}
        orientation="horizontal"
        style={{ position: "absolute", left: 70, bottom: -20 }}
      />
    </XYPlot>
  );
};

const ConsumerPlot = (props) => {
  const { width, height, stores } = props;

  const data1 = stores[0]["Local Consumer"].map((g, index) => ({
    y: -0.5 - index,
    x: g,
  }));

  const labelData1 = stores[0]["Local Consumer"].map((g, index) => ({
    y: -0.5 - index,
    x: g / 2,
    label: `${g}%`,
  }));

  const data2 = stores[0]["Local Consumer"].map((g, index) => ({
    y: -0.5 - index,
    x: 100 - g,
  }));

  const labelData2 = stores[0]["Local Consumer"].map((g, index) => ({
    y: -0.5 - index,
    x: 50, // g + (100 - g) / 2 - g / 2,
    label: `${Number(round(100 - g).toFixed(2))}%`,
  }));

  const legend = [
    {
      title: "Local consumer",
      color: legendColors[0],
      strokeWidth: 24,
    },
    { title: "Tourist consumer", color: legendColors[4], strokeWidth: 24 },
  ];

  return (
    <XYPlot
      width={width - 10}
      height={height - 20}
      margin={{ left: 90, top: 50 }}
      stackBy="x"
      xDomain={[0, 100]}
      yDomain={[-7, 0]}
    >
      <VerticalGridLines />
      <HorizontalGridLines />
      <XAxis
        hideTicks
        orientation="top"
        style={{
          line: { stroke: "black", strokeWidth: 1 },
        }}
      />
      <ChartLabel
        text="Consumer group"
        includeMargin={true}
        xPercent={0.12}
        yPercent={-0.08}
      />
      <YAxis
        style={{
          line: { stroke: "black", strokeWidth: 1 },
          text: {
            stroke: "none",
            fill: "black",
            fontSize: 14,
            fontWeight: 400,
          },
        }}
        tickValues={[-0.5, -1.5, -2.5, -3.5, -4.5, -5.5, -6.5]}
        tickFormat={(t) => days[t + 6.5]}
      />
      <HorizontalBarSeries
        color={legendColors[0]}
        data={data1}
        barWidth={0.6}
        style={{
          text: { fontWeight: 800 },
        }}
      />
      <LabelSeries
        data={labelData1}
        labelAnchorX="middle"
        labelAnchorY="middle"
        style={{ fill: "white", fontSize: 14, fontWeight: 400 }}
      />
      <HorizontalBarSeries
        color={legendColors[4]}
        data={data2}
        barWidth={0.6}
        style={{
          text: { fontWeight: 800 },
        }}
      />
      <LabelSeries
        data={labelData2}
        labelAnchorX="middle"
        labelAnchorY="middle"
        style={{ fill: "black", fontSize: 14, fontWeight: 400 }}
      />
      <DiscreteColorLegend
        items={legend}
        orientation="horizontal"
        style={{ position: "absolute", left: 70, bottom: -20 }}
      />
    </XYPlot>
  );
};

const DailyPatterns = (props) => {
  const { stores, selectedAttributes } = props;
  return (
    <AutoSizer>
      {({ width, height }) => (
        <>
          {selectedAttributes.includes("Gender") && (
            <GenderPlot
              width={width}
              height={height}
              stores={stores}
            ></GenderPlot>
          )}
          {selectedAttributes.includes("Age group") && (
            <AgePlot width={width} height={height} stores={stores}></AgePlot>
          )}
          {selectedAttributes.includes("Income group") && (
            <IncomePlot
              width={width}
              height={height}
              stores={stores}
            ></IncomePlot>
          )}
          {selectedAttributes.includes("Consumer group") && (
            <ConsumerPlot
              width={width}
              height={height}
              stores={stores}
            ></ConsumerPlot>
          )}
        </>
      )}
    </AutoSizer>
  );
};

export default React.memo(DailyPatterns);

/*
        (selectedAttributes[0] === "Gender" && (
          <GenderPlot
            width={width}
            height={height}
            stores={stores}
          ></GenderPlot>
        )) ||
        (selectedAttributes[0] === "Age group" && (
          <AgePlot width={width} height={height} stores={stores}></AgePlot>
        )) ||
        (selectedAttributes[0] === "Income group" && (
          <IncomePlot
            width={width}
            height={height}
            stores={stores}
          ></IncomePlot>
        )) || (
          <ConsumerPlot
            width={width}
            height={height}
            stores={stores}
          ></ConsumerPlot>
        )
 */
